import { CobaltPagination } from '@cae/cobalt-react/components/pagination';
import {
  CobaltTable,
  CobaltTableBody,
  CobaltTableCell,
  CobaltTableContainer,
  CobaltTableHead,
  CobaltTableRow,
  CobaltTableSortLabel,
} from '@cae/cobalt-react/components/table';
import { CobaltTag } from '@cae/cobalt-react/components/tag';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { SortDirection } from '@features/users/contexts/data/UsersDataContext.types';
import { useUsersData } from '@features/users/contexts/data/useUsersData';
import { useUserNavigation } from '@features/users/hooks/use-user-navigation/useUserNavigation';
import { DataValue } from '@shared/components/data-value/DataValue';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import * as React from 'react';
import { useMatch } from 'react-router-dom';

import styles from './UsersTable.module.css';
import { TABLE_COLUMNS, getUsersStatusTagColor } from './UsersTable.utils';

const UsersTable = React.memo((): React.ReactElement => {
  const {
    users,
    pageSize,
    pageIndex,
    sortBy,
    sortDirection,
    handlePageIndexChange,
    handlePageSizeChange,
    handleSortChange,
  } = useUsersData();
  const { email } = useAuthenticatedUser();
  const { navigateToUser } = useUserNavigation();
  const userPanelMatch = useMatch('/manage/:customerId/users/:userId');
  const selectedUserId = userPanelMatch?.params.userId;

  const { data } = users;
  const totalItems = data.total;

  const handleRequestSort = React.useCallback(
    (columnId: string) =>
      (direction: SortDirection): void => {
        handleSortChange(columnId, direction);
      },
    [handleSortChange]
  );

  return (
    <CobaltTableContainer
      pagination={
        <CobaltPagination
          length={totalItems}
          pageSize={pageSize}
          pageIndex={pageIndex}
          onPageIndexChange={handlePageIndexChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 25, 50, 100]}
          className={styles.usersTablePagination}
        />
      }
      className={styles.usersTableContainer}
      data-testid="users-table"
    >
      <CobaltTable size="large" key={`table-${sortBy}-${sortDirection}`}>
        <CobaltTableHead>
          <CobaltTableRow>
            {TABLE_COLUMNS.map(column => (
              <CobaltTableCell
                key={column.id}
                header
                style={{ width: column.width }}
              >
                {column.sortable ? (
                  <CobaltTableSortLabel
                    active={sortBy === column.id}
                    direction={
                      sortBy === column.id && sortDirection !== false
                        ? sortDirection
                        : undefined
                    }
                    onDirectionChange={handleRequestSort(column.id)}
                  >
                    {column.label}
                  </CobaltTableSortLabel>
                ) : (
                  column.label
                )}
              </CobaltTableCell>
            ))}
          </CobaltTableRow>
        </CobaltTableHead>
        <CobaltTableBody>
          {data.elements.map(user => {
            const isCurrentUser = email === user.email;

            return (
              <CobaltTableRow
                key={user.id}
                onClick={(): void => navigateToUser(user.id)}
                interactive
                selected={user.id === selectedUserId}
              >
                <CobaltTableCell>
                  <DataValue value={user.name} />
                  {isCurrentUser && user.name && ' (you)'}
                </CobaltTableCell>
                <CobaltTableCell>
                  <DataValue value={user.email} />
                </CobaltTableCell>
                <CobaltTableCell>
                  <DataValue
                    value={user.roles ? user.roles.join(', ') : null}
                  />
                </CobaltTableCell>
                <CobaltTableCell>
                  <DataValue
                    value={
                      user.lastLogin ? formatDateShort(user.lastLogin) : null
                    }
                    fallback="-"
                  />
                </CobaltTableCell>
                <CobaltTableCell>
                  <DataValue
                    value={
                      user.lastModified
                        ? formatDateShort(user.lastModified)
                        : null
                    }
                  />
                </CobaltTableCell>
                <CobaltTableCell>
                  {user.status ? (
                    <CobaltTag
                      color={getUsersStatusTagColor(user.status)}
                      size="small"
                    >
                      {user.status}
                    </CobaltTag>
                  ) : (
                    <DataValue value={null} />
                  )}
                </CobaltTableCell>
              </CobaltTableRow>
            );
          })}
        </CobaltTableBody>
      </CobaltTable>
    </CobaltTableContainer>
  );
});

UsersTable.displayName = 'UsersTable';

export { UsersTable };
